// File: packages/ui/src/Card.tsx

import React, { CSSProperties } from 'react';
import clsx from 'clsx';
import { motion, Transition } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { renderIcon } from '@dopomogai/utils';

interface CardProps {
  title?: string;
  titleKey?: string;
  titleOptions?: Record<string, unknown>;
  className?: string;
  children: React.ReactNode;
  actionButton?: React.ReactNode;
  id?: string;
  icon?: React.ReactNode | string;
  headerBgColor?: string;
  noPadding?: boolean;
  noHoverEffect?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
}

// Neobrutalism transition
const neobrutalismTransition: Transition = {
  type: "tween",
  duration: 0.1,
  ease: "easeOut"
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({
    title: directTitle,
    titleKey,
    titleOptions,
    className,
    children,
    actionButton,
    id,
    icon,
    headerBgColor = 'bg-white',
    noPadding = false,
    noHoverEffect = false,
    style,
    onClick,
  }, ref) => {
    const { t } = useTranslation(['public_showcase', 'solution_showcase']);
    const resolvedTitle = titleKey ? t(titleKey, titleOptions) : directTitle;
    const hasHeader = resolvedTitle || actionButton || icon;

    const isInteractive = !noHoverEffect && !!onClick;

    const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
      if (!isInteractive || event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick?.();
      }
    };

    // Classes that support both themes
    const cardClasses = clsx(
      // Base structure
      "overflow-hidden mb-8",

      // Neobrutalism (default)
      "bg-white rounded-xl border-2 border-black",
      "focus-within:ring-2 focus-within:ring-emerald-500 focus-within:ring-offset-2",

      // Liquid glass overrides
      "[.theme-liquid-glass_&]:bg-white/10",
      "[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20",
      "[.theme-liquid-glass_&]:rounded-3xl",
      "[.theme-liquid-glass_&]:backdrop-blur-xl",
      "[.theme-liquid-glass_&]:focus-within:ring-violet-400",
      "[.theme-liquid-glass_&]:focus-within:ring-offset-slate-900",

      isInteractive && "cursor-pointer",
      className
    );

    const headerClasses = clsx(
      "flex justify-between items-center px-6 py-4",
      // Neobrutalism
      "border-b-2 border-black",
      headerBgColor,
      // Liquid glass
      "[.theme-liquid-glass_&]:border-b [.theme-liquid-glass_&]:border-white/20",
      "[.theme-liquid-glass_&]:bg-white/5"
    );

    const titleClasses = clsx(
      "text-xl md:text-2xl font-extrabold",
      // Neobrutalism
      "text-slate-900",
      // Liquid glass
      "[.theme-liquid-glass_&]:text-white"
    );

    const iconClasses = clsx(
      "flex items-center shrink-0",
      // Neobrutalism
      "text-gray-700",
      // Liquid glass
      "[.theme-liquid-glass_&]:text-white/80"
    );

    const hoverTranslateX = 'var(--ui-hover-translate-x, -4px)';
    const hoverTranslateY = 'var(--ui-hover-translate-y, -4px)';
    const hoverShadow = 'var(--ui-shadow-hover, 4px 4px 0px 0px rgb(17, 24, 39))';
    const activeShadow = 'var(--ui-shadow-active, none)';

    return (
      <motion.section
        id={id}
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        whileHover={isInteractive ? {
          x: hoverTranslateX,
          y: hoverTranslateY,
          boxShadow: hoverShadow,
          transition: neobrutalismTransition,
        } : {}}
        whileTap={isInteractive ? {
          x: 0,
          y: 0,
          boxShadow: activeShadow,
          scale: 0.99,
          transition: neobrutalismTransition,
        } : {}}
        style={style}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        className={cardClasses}
      >
        {hasHeader && (
          <div className={headerClasses}>
            <div className="flex items-center gap-3">
              {icon && <span className={iconClasses}>{renderIcon(icon)}</span>}
              {resolvedTitle && <h2 className={titleClasses}>{resolvedTitle}</h2>}
            </div>
            {actionButton && <div className="flex items-center ml-auto pl-4 shrink-0">{actionButton}</div>}
          </div>
        )}
        <div className={clsx(!noPadding && "p-6 md:p-8")}>
          {children}
        </div>
      </motion.section>
    );
  }
);
Card.displayName = 'Card';
